import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImageIcon, Trash2, ShieldAlert, AlertTriangle, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadChartPhoto, fetchChartPhotoBlob } from '../entriesApi';

/**
 * Slice D Step 2 — Photo Evidence renderer (private upload + gated thumbnail).
 * Emits the B.1 backend contract: { url, mime, uploaded_at } where `url` is a
 * GATED reference (/api/participants/.../photos/{id}) — never a public/static URL.
 *
 * The thumbnail is fetched as an authenticated blob (the gated endpoint enforces
 * RBAC + B.3 high-risk redaction), so unauthorised users never receive bytes.
 * When the viewer lacks high-risk permission the field renders "[Redacted]".
 *
 * UAT-ONLY: files are stored on private local disk. Migrate to durable cloud
 * object storage before production/provider/clinical rollout.
 */

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

export function PhotoEvidenceField({ value, onChange, canViewHighRisk = true, participantId, chartId }) {
  const v = value && typeof value === 'object' ? value : {};
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [thumb, setThumb] = useState(null);

  // Fetch a gated thumbnail blob whenever the stored url changes.
  useEffect(() => {
    let revoked = null;
    let cancelled = false;
    async function load() {
      setThumb(null);
      if (!v.url) return;
      try {
        const blob = await fetchChartPhotoBlob(v.url);
        if (cancelled) return;
        const obj = URL.createObjectURL(blob);
        revoked = obj;
        setThumb(obj);
      } catch {
        if (!cancelled) setThumb(null);
      }
    }
    load();
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [v.url]);

  if (!canViewHighRisk) {
    return (
      <div className="rounded-md border border-dashed bg-slate-50 p-3 text-xs text-slate-500 flex items-center gap-2"
           data-testid="chart-field-photo-evidence">
        <ShieldAlert className="w-4 h-4" /> [Redacted] — high-risk view permission required.
      </div>
    );
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error('Only JPEG, PNG or WebP images are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image exceeds the 5 MB limit');
      return;
    }
    if (!participantId || !chartId) {
      toast.error('Save the chart before adding photo evidence');
      return;
    }
    try {
      setUploading(true);
      const meta = await uploadChartPhoto(participantId, chartId, file);
      onChange({ url: meta.url, mime: meta.mime, uploaded_at: meta.uploaded_at });
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clear = () => { onChange(null); setThumb(null); };

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="chart-field-photo-evidence">
      <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        <AlertTriangle className="w-3.5 h-3.5" />
        UAT-only: photos are stored privately and access-controlled. Cloud storage migration pending before production.
      </div>

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
             className="hidden" onChange={handleFile} data-testid="chart-field-photo-evidence-input" />

      {!v.url ? (
        <Button type="button" variant="outline" size="sm" disabled={uploading}
                onClick={() => inputRef.current?.click()} data-testid="chart-field-photo-evidence-upload">
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          {uploading ? 'Uploading…' : 'Upload photo (JPEG/PNG/WebP, ≤5 MB)'}
        </Button>
      ) : (
        <div className="flex items-center gap-3 rounded border bg-slate-50 p-2">
          {thumb ? (
            <img src={thumb} alt="evidence" className="w-16 h-16 object-cover rounded border bg-white"
                 data-testid="chart-field-photo-evidence-thumb" />
          ) : (
            <div className="w-16 h-16 rounded border bg-white flex items-center justify-center text-slate-400">
              <ImageIcon className="w-5 h-5" />
            </div>
          )}
          <div className="text-[11px] text-slate-500 flex-1 break-all">{v.mime || 'image'}</div>
          <Button type="button" variant="outline" size="sm" className="h-8 text-red-600"
                  onClick={clear} data-testid="chart-field-photo-evidence-clear">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default PhotoEvidenceField;
